import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

import { AuthShell } from './AuthShell';
import { signupSchema, type SignupInput } from './schemas';
import { supabase } from '../../lib/supabase';
import { logAudit } from '../../lib/audit';
import { cn } from '../../lib/cn';
import { useToast } from '../../components/Toast';
import styles from './auth.module.css';

export function SignupScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: { email: '', password: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/verify`,
      },
    });
    if (error) {
      if (error.message.toLowerCase().includes('already')) {
        setFormError('כבר קיים חשבון עם האימייל הזה');
      } else {
        setFormError('לא הצלחנו ליצור חשבון — נסה שוב');
      }
      return;
    }
    await logAudit({ action: 'auth.signed_up' });
    toast.success('שלחנו מייל אימות');
    navigate('/auth/verify-pending', { state: { email } });
  });

  return (
    <AuthShell
      title="צור חשבון חדש"
      subtitle="התחל לעבוד ב-FocusMode OS תוך דקה"
      brandSide={{
        title: 'התחל בקטן · גדל איתנו',
        body: 'חשבון אחד, כל המודולים. ללא אשראי, ללא טריאל מוגבל בזמן.',
      }}
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <div className={styles.field}>
          <label htmlFor="signup-email">אימייל</label>
          <input
            id="signup-email"
            type="email"
            dir="ltr"
            autoComplete="email"
            placeholder="you@company.co.il"
            className={cn(styles.input, errors.email && styles.invalid)}
            {...register('email')}
          />
          {errors.email ? <p className={styles.error}>{errors.email.message}</p> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="signup-password">סיסמה</label>
          <div className={styles.fieldInput}>
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              dir="ltr"
              autoComplete="new-password"
              placeholder="לפחות 8 תווים, אות וספרה"
              className={cn(styles.input, errors.password && styles.invalid)}
              {...register('password')}
            />
            <button
              type="button"
              className={styles.eye}
              onClick={() => { setShowPassword((v) => !v); }}
              aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password ? <p className={styles.error}>{errors.password.message}</p> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="signup-confirm">אימות סיסמה</label>
          <input
            id="signup-confirm"
            type={showPassword ? 'text' : 'password'}
            dir="ltr"
            autoComplete="new-password"
            placeholder="הקלד שוב"
            className={cn(styles.input, errors.confirmPassword && styles.invalid)}
            {...register('confirmPassword')}
          />
          {errors.confirmPassword ? (
            <p className={styles.error}>{errors.confirmPassword.message}</p>
          ) : null}
        </div>

        {formError ? <p className={styles.formError}>{formError}</p> : null}

        <button type="submit" className={styles.primary} disabled={isSubmitting}>
          צור חשבון
          <ArrowLeft size={14} className="flip-rtl" />
        </button>

        <p className={styles.switch}>
          כבר יש לך חשבון?{' '}
          <Link to="/auth/login" className={styles.link}>
            התחבר
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
